import React, {useEffect, useState} from "react";
import TextInput from '../../Form/TextInput.tsx';
import Select from '../../Form/Select.tsx';
import Datepicker from '../../Form/Datepicker.tsx';
import { IProfile } from '../../../interface/IProfile.ts';
import { useProfileApi } from '../../../features/auth/hooks/use-profile-api.ts'
import { ApiResponseType } from "../../../features/api/types.ts";
import Form from "../../Form/Form.tsx";
import { useListsApi } from "../../../features/auth/hooks/use-lists-api.ts";
import { IOption } from "../../../interface/IOption.ts";
import ApiCheck from "../../Structure/ApiCheck.tsx";

const TabPersonalData = ({
    profile,
    setProfile
}:{
    profile:IProfile
    setProfile: Function
}) => {
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [responseMessage, setResponseMessage] = useState<string>('');
    const [responseMessageClass, setResponseMessageClass] = useState<string>('');
    const { postProfile } = useProfileApi();
    const [errors, setErrors] = useState<any>({});
    const [unSavedChanges, setUnSavedChanges] = useState(false);
    const { getList } = useListsApi();
    const [titleOptions, setTitleOptions] = useState<Array<IOption>>();
    const [titlesLoading, setTitlesLoading] = useState<boolean>(true);
    const [apiErrors, setApiErrors] = useState<any>();

    const salutationOptions = [
        {
            value: '0',
            label: 'Herr'
        },
        {
            value: '1',
            label: 'Frau'
        },
        {
            value: '2',
            label: 'Divers'
        }
    ];

    const genderOptions = [
        {
            value: '0',
            label: 'männlich'
        },
        {
            value: '1',
            label: 'weiblich'
        },
        {
            value: '2',
            label: 'divers'
        }
    ];

    // Salutation is not editable, it always follows the gender
    // eslint-disable-next-line
    const getSalutationForGender = (gender:string): any => {
        let genderIndex = genderOptions.findIndex(x => x.value === gender);
        return genderIndex !== -1 ? salutationOptions[genderIndex] : undefined;
    }

    const initialGender = profile.contact_gender ? String(profile.contact_gender) : '0';

    const [formData, setFormData] = useState<IProfile>({
        salutation: getSalutationForGender(initialGender),
        title: profile.title,
        firstname: profile.firstname,
        birth_name: profile.birth_name,
        lastname: profile.lastname,
        name_addition: profile.name_addition,
        contact_date_of_birth: Number(profile.contact_date_of_birth) * 1000,
        contact_gender: initialGender,
    });

    const handleChange = (event, name = '', value = null) => {
        setFormData({ ...formData, [name ? name : event.target.name]: value ? value : event.target.value });
        !unSavedChanges && setUnSavedChanges(true);
    };

    const handleGenderChange = (value:string) => {
        let formDataClone = structuredClone(formData);
        formDataClone['contact_gender'] = value;
        formDataClone['salutation'] = getSalutationForGender(value);
        setFormData(formDataClone);
    }

    const sortByOrderNumber = ( a:any, b:any ) => {
        return a.orderNumber - b.orderNumber;
    }


    useEffect(() => {
        getList('title').then(res => {
            res.sort( sortByOrderNumber );
            let optionList:Array<IOption> = [];
            if (res) {
                res.forEach((item:any) => {
                    optionList.push({
                        value: item.id,
                        label: item.label
                    });
                });
                setTitleOptions(optionList);
                let titlesFromOptions:Array<IOption> = [];
                titlesFromOptions = optionList.filter(singleTitleOption => {
                    return profile.title === singleTitleOption.value;
                })
                if (titlesFromOptions.length > 0) {
                    setFormData(prev => ({ ...prev, title: titlesFromOptions[0] }));
                }
                setTitlesLoading(false);
            }
        }).catch(() => {
            setApiErrors({
                errors: 'Der Server ist aktuell nicht erreichbar. Bitte probieren Sie es später erneut.'
            });
            setTitlesLoading(false);
        });
    }, []);

    const handleSubmit = (event) => {
        event.preventDefault();
        setUnSavedChanges(false);
        setIsLoading(true);

        const submittedData = structuredClone(formData);
        if (typeof submittedData.contact_date_of_birth === 'object') {
            let dateValue = Math.floor(submittedData.contact_date_of_birth!.getTime() / 1000);
            submittedData.contact_date_of_birth = dateValue;
        }

        if (submittedData.salutation && typeof submittedData.salutation === 'object') {
            submittedData.salutation = submittedData.salutation.value;
        }

        if (submittedData.title && typeof submittedData.title === 'object') {
            submittedData.title = submittedData.title.value;
        }

        delete submittedData.firstname;
        delete submittedData.lastname;
        delete submittedData.contact_date_of_birth;

        setErrors({});
        postProfile(submittedData).then(res => {
            setIsLoading(false);
            if (res.type === ApiResponseType.OK) {
                if (res.payload.success)  {                    
                    setProfile({...profile, ...submittedData});
                    setResponseMessage('Erfolgreich gespeichert');
                    setResponseMessageClass('success');
                    setTimeout(() => {
                        setResponseMessage('');
                    }, 5000);
                }
            } else {
                let errors = res.payload.errors!;
                if (!errors.general_error) {
                    setResponseMessage('Fehler. Bitte korrigieren Sie den Fehler und versuchen Sie es erneut.');
                    setResponseMessageClass('error');
                }
                setErrors(errors);
            }
        })
    }

    return (
        <div className="single-tab-item">
            <Form
                unSavedChanges={unSavedChanges}
                handleSubmit={handleSubmit}
                isLoading={isLoading}
                responseMessage={responseMessage}
                responseMessageClass={responseMessageClass}
                generalErrors={ errors.general_error ? errors.general_error : null}
                displayRequiredHint={true}
            >
                <Select
                    isSearchable={false}
                    isClearable={false}
                    label='Geschlecht'
                    placeholder='Geschlecht auswählen'
                    name='contact_gender'
                    onChange={(event) => handleGenderChange(event.value)}
                    defaultValue={formData?.contact_gender}
                    options={genderOptions}
                    error={errors['contact_gender']}
                    isRequired={true}
                />
                <Select
                    isSearchable={false}
                    isClearable={false}
                    label='Anrede'
                    placeholder='Anrede auswählen'
                    name='salutation'
                    value={formData?.salutation}
                    options={salutationOptions}
                    error={errors['salutation']}
                    isRequired={false}
                    isDisabled={true}
                />
                { titlesLoading ? (
                    <div className="form-group loading">
                        <label htmlFor="title">Titel</label>
                        <input disabled></input>
                        <div className="loader"></div>
                    </div>
                ) : (
                    <ApiCheck errors={apiErrors ? apiErrors : null}>
                        <Select
                            onChange={(event) => handleChange(event, 'title', event.value)}
                            defaultValue={formData?.title}
                            label='Titel'
                            placeholder='kein Titel'
                            name='title'
                            isSearchable={false}
                            isClearable={true}
                            isDisabled={false}
                            options={titleOptions}
                            error={errors['title']}
                        />
                    </ApiCheck>
                ) }
                <TextInput
                    onChange={handleChange}
                    value={formData?.firstname}
                    label='Vorname & Mittelname'
                    required={true}
                    name='firstname'
                    disabled={true}
                    error={errors['firstname']}
                />
                <TextInput
                    onChange={handleChange}
                    value={formData?.lastname}
                    label='Nachname'
                    required={true}
                    name='lastname'
                    disabled={true}
                    error={errors['lastname']}
                />
                <TextInput
                    onChange={handleChange}
                    value={formData?.birth_name}
                    label='Geburtsname'
                    name='birth_name'
                    disabled={false}
                    error={errors['birth_name']}
                />
                <Datepicker
                    selected={formData?.contact_date_of_birth}
                    onChange={(date) => {
                        handleChange(null, 'contact_date_of_birth', date);
                    }}
                    locale='de'
                    dateFormat='dd.MM.yyyy'
                    showYearDropdown={true}
                    showMonthDropdown={true}
                    maxDate={new Date()}
                    label='Geburtsdatum'
                    isRequired={true}
                    name='contact_date_of_birth'
                    disabled={true}
                    error={errors['contact_date_of_birth']}
                />
                <TextInput
                    onChange={handleChange}
                    value={formData?.name_addition}
                    label='Namenszusatz'
                    name='name_addition'
                    error={errors['name_addition']}
                />
            </Form>
        </div>
    )
}

export default TabPersonalData;